import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Documento } from "../../../shared/types";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { CampoBusqueda } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api } from "../../lib/apiClient";

type DocumentoConExpediente = Documento & { numero_expediente: string | null; cliente_nombre: string | null };

function formatearPeso(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Búsqueda global de solo lectura: encontrar un archivo sin saber a qué
// expediente pertenece. Subir/eliminar documentos vive exclusivamente en
// la pestaña "Documentos" de cada expediente (PestanaDocumentos.tsx) — acá
// no se duplica esa gestión.
export function DocumentosPage() {
  const { mostrar } = useToast();
  const [documentos, setDocumentos] = useState<DocumentoConExpediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    setCargando(true);
    api
      .get<{ items: DocumentoConExpediente[] }>("/documentos")
      .then((d) => setDocumentos(d.items))
      .catch(() => mostrar("No se pudieron cargar los documentos.", "error"))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtrados = documentos.filter((d) =>
    `${d.titulo} ${d.numero_expediente ?? ""} ${d.categoria ?? ""} ${d.cliente_nombre ?? ""}`
      .toLowerCase()
      .includes(busqueda.toLowerCase()),
  );

  const columnas: ColumnaTabla<DocumentoConExpediente>[] = [
    { encabezado: "Título", render: (d) => d.titulo },
    {
      encabezado: "Cliente",
      render: (d) => d.cliente_nombre ?? "—",
    },
    {
      encabezado: "Expediente",
      render: (d) =>
        d.expediente_id ? (
          <Link
            to={`/expedientes/${d.expediente_id}`}
            className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
          >
            {d.numero_expediente}
          </Link>
        ) : (
          "—"
        ),
    },
    {
      encabezado: "Categoría",
      render: (d) => (d.categoria ? <Insignia texto={d.categoria} /> : "—"),
    },
    { encabezado: "Fecha", render: (d) => d.creado_en.substring(0, 10) },
    { encabezado: "Peso", render: (d) => formatearPeso(d.peso_bytes) },
    {
      encabezado: "Acciones",
      render: (d) => (
        <a
          href={`/api/documentos/${d.id}/descargar`}
          className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Descargar
        </a>
      ),
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Buscar documentos"
        descripcion="Búsqueda global de documentos de todos los expedientes. Para subir o eliminar un documento, hacelo desde la pestaña Documentos del expediente correspondiente."
      />
      <div className="mb-4">
        <CampoBusqueda
          valor={busqueda}
          onCambio={setBusqueda}
          placeholder="Buscar por título, cliente, expediente o categoría..."
        />
      </div>
      <Tabla
        columnas={columnas}
        filas={filtrados}
        claveFila={(d) => d.id}
        cargando={cargando}
        tituloVacio="No hay documentos"
        descripcionVacio="Los documentos se suben desde la pestaña Documentos de cada expediente."
      />
    </div>
  );
}
